import { Box, Stack } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Card } from '@/components/layout/Card/Card';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Header } from '@/components/navigation/Header';
import { useModalState } from '@/hooks/useModalState';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { GrowthProfileModal } from '@/modules/growthProfiles/GrowthProfileModal/GrowthProfileModal';
import { Operations as GrowthProfilesTableOperations } from '@/modules/growthProfiles/GrowthProfilesTrowser/GrowthProfilesTable/graphql/GrowthProfilesTable.generated';
import { GrowthProfilesTable } from '@/modules/growthProfiles/GrowthProfilesTrowser/GrowthProfilesTable/GrowthProfilesTable';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { COLORS } from '@/styles/tokens/colors';

/**
 * Surfaces that open the growth profiles list. Each keeps its own mount and
 * gates it on the target below, so a click opens only the copy that was
 * clicked: several of these mount many times over (one per recipient row, one
 * per assumptions slide) or coexist on a page, and open state is shared through
 * the URL.
 */
export enum GrowthProfilesTrowserScope {
  GrowAllByInflationMenu = 'growAllByInflationMenu',
  GrowByInflationHelp = 'growByInflationHelp',
  HypotheticalGratCallout = 'hypotheticalGratCallout',
  PresentationAssumptions = 'presentationAssumptions',
  WaterfallAssumptions = 'waterfallAssumptions',
}

/**
 * The {@link TrowserKey.GrowthProfiles} `target`: the opening surface plus an id
 * for that surface's instance (a field path, a waterfall, a bundle). Every
 * instance shows the same household-scoped list, so this exists only to pick
 * out which mount should render. Hosts build it for both opening and for their
 * own render gate, so no parsing is needed.
 */
export function growthProfilesTrowserTarget(
  scope: GrowthProfilesTrowserScope,
  instanceId?: string | null
): string {
  return instanceId ? `${scope}:${instanceId}` : scope;
}

export interface GrowthProfilesTrowserProps {
  onClose: () => void;
  householdId: string;
  /**
   * Extra query names to refetch after a profile is created, edited,
   * duplicated, or deleted in here. Mutations inside the trowser refetch its
   * own table, so a host that lists profiles through a different query names it
   * here to keep its own copy current.
   */
  refetchQueries?: string[];
}

/**
 * Full-screen list of the household's growth profiles. Open state is URL-driven
 * ({@link TrowserKey.GrowthProfiles}), so it deep-links, survives reload, and
 * closes on browser Back. Hosts render it only while their own target is open.
 */
export function GrowthProfilesTrowser({
  onClose,
  householdId,
  refetchQueries = [],
}: GrowthProfilesTrowserProps) {
  const canEdit = useCanEditHouseholdData();
  const [
    { isModalOpen: isCreateModalOpen },
    { openModal: openCreateModal, closeModal: closeCreateModal },
  ] = useModalState();

  // The table this trowser shows always refetches; hosts add their own list.
  const refetchQueriesWithTable = [
    GrowthProfilesTableOperations.Query.GrowthProfilesTable,
    ...refetchQueries,
  ];

  return (
    <FullScreenModal
      trowserKey={TrowserKey.GrowthProfiles}
      isOpen
      onClose={onClose}
    >
      {isCreateModalOpen && (
        <GrowthProfileModal
          onClose={closeCreateModal}
          householdId={householdId}
          refetchQueries={refetchQueriesWithTable}
        />
      )}
      <Box>
        <Stack height="100vh" overflow="auto">
          <Header
            title="Growth profiles"
            rightHeaderContent={
              <Stack direction="row" spacing={1}>
                {canEdit && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openCreateModal}
                    startIcon={PlusIcon}
                  >
                    Create
                  </Button>
                )}
                <Button variant="primary" size="sm" onClick={onClose}>
                  Done
                </Button>
              </Stack>
            }
          />
          <Box p={2} flex={1} bgcolor={COLORS.GRAY[50]}>
            <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
              <GrowthProfilesTable
                householdId={householdId}
                refetchQueries={refetchQueriesWithTable}
              />
            </Card>
          </Box>
        </Stack>
      </Box>
    </FullScreenModal>
  );
}
